import {
  ApiError,
  ApiParsingUtilities,
  DomainDataObjects,
  IUserProviderState,
  JemConfiguration,
  postRequest
} from "@jem/components";
import { faker } from "@faker-js/faker";
import { v4 } from "uuid";
import { GLBatchSearchCriteria } from "../GLBatchSearch.types";

export interface BatchLineInfo {
  bpoBatch: boolean;
  batchName: string;
  description: string;
  errorPostings: number;
  isHavingAttachments: number;
  postingDate: string;
  refGuid: string;
  successPostings: number;
  totalPostings: number;
  checkListName: string;
  opsDetailsName: string;
  isAdhocOrStd: string;
}

export interface BatchLineInfoModel extends BatchLineInfo {
  postingComplete: boolean;
  hasAttachments: boolean;
}
export interface BatchAILineInfo {
  bpoBatch: boolean;
  batchName: string;
  description: string;
  errorPostings: number;
  isHavingAttachments: number;
  jeDate: string;
  refGUID: string;
  successPostings: number;
  totalPostings: number;
  jeCheckListName: string;
  opsDetailsName: string;
  isAdhocOrStd: string;
  batchGuid: string;
}
export interface BatchAIsearchResult {
  searchInfos: BatchAILineInfo[];
  totalCount?: number;
  totalPages?: number;
  nextPageIndex?: number;
  uiLimit?: number;
  exportLimit?: number;
  searchType?: string;
}

export interface BatchPayloadSchema {
  Alias: string[];
  AliasType: string;
  FiscalYear: number;
  FiscalValue: FiscalValue[];
  BatchName: string | null;
  OpsDetailId: OpsDetailId[];
  UILimit: number;
  TotalCount: number;
  NextPageIndex: number;
  AllowAISearchinUI?: boolean;
  BatchGuid?: string | null;
}

export interface OpsDetailId {
  OpsId: number;
}

export interface FiscalValue {
  FiscalYear: number;
  FiscalMonth: number;
}

export function createRandomBatchLineInfo(): BatchLineInfo {
  const total = faker.datatype.number(200);
  const errors = faker.datatype.number(100);
  const success = total - errors;
  return {
    bpoBatch: faker.datatype.boolean(),
    batchName: faker.lorem.words(2),
    description: faker.lorem.words(3),
    errorPostings: errors,
    isHavingAttachments: faker.datatype.number(50),
    postingDate: faker.date.recent().toISOString(),
    refGuid: faker.datatype.uuid(),
    successPostings: success,
    totalPostings: total
  } as BatchLineInfo;
}

export async function fetchSearchResults(
  configuration: JemConfiguration["GeneralLedgerApi"],
  accessToken: IUserProviderState["accessToken"],
  domainData: DomainDataObjects | null,
  searchCriteria: GLBatchSearchCriteria
): Promise<BatchLineInfoModel[]> {
  const payload = userSelectionToPayload(searchCriteria);

  const endpointUrl =
    payload.AllowAISearchinUI === true
      ? `${configuration.baseApiUrl}${configuration.endpoints.getAIBatchSearchResults}`.replace("{refguid}", v4())
      : `${configuration.baseApiUrl}${configuration.endpoints.getBatchSearchResults}`.replace("{refguid}", v4());
  const response = await postRequest<BatchLineInfo[] | BatchAIsearchResult>(endpointUrl, payload, accessToken);
  if (!response) {
    throw new ApiError("Error from Server.");
  }

  if (Array.isArray(response)) {
    return response.map(transformBatchLineInfoIntoModel);
  } else if (response.searchInfos) {
    return response.searchInfos.map(transformBatchLineInfoIntoModel);
  } else {
    throw new ApiError("Unexpected response format.");
  }
}

function userSelectionToPayload(searchCriteria: GLBatchSearchCriteria): BatchPayloadSchema {
  const payload: BatchPayloadSchema = {
    Alias: [searchCriteria.Alias],
    AliasType: searchCriteria.AliasType === "P" ? "1" : "2",
    FiscalYear: searchCriteria.FiscalYear,
    FiscalValue: (searchCriteria.FiscalMonth.includes(-1)
      ? []
      : searchCriteria.FiscalMonth.includes(0)
      ? Array.from({ length: 12 }, (_, i) => i + 1)
      : searchCriteria.FiscalMonth
    ).map((fm) => ({ FiscalYear: searchCriteria.FiscalYear, FiscalMonth: fm })),
    BatchName: searchCriteria.BatchName,
    OpsDetailId: searchCriteria.OpsDetailId
      ? (searchCriteria.OpsDetailId.includes(-1) ? [] : searchCriteria.OpsDetailId).map((fm) => ({ OpsId: fm }))
      : [],
    UILimit: searchCriteria.pageSize,
    TotalCount: searchCriteria.totalCount || 0,
    NextPageIndex: searchCriteria.nextPageIndex || 0,
    AllowAISearchinUI: searchCriteria.IsAISearch,
    ...(searchCriteria.BatchGuid ? { BatchGuid: searchCriteria.BatchGuid } : {})
  };
  return payload;
}

function transformBatchLineInfoIntoModel(apiData: BatchLineInfo | BatchAILineInfo): BatchLineInfoModel {
  return {
    ...apiData,
    refGuid: "refGuid" in apiData ? apiData.refGuid : apiData.batchGuid,
    postingDate: ApiParsingUtilities.parseDateWithTimeStamp(
      "postingDate" in apiData ? apiData.postingDate : apiData.jeDate
    ),
    checkListName: "checkListName" in apiData ? apiData.checkListName : apiData.jeCheckListName,
    postingComplete: apiData.successPostings + apiData.errorPostings === apiData.totalPostings,
    hasAttachments: apiData.isHavingAttachments > 0
  };
}
